import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { PageLayout } from "@/components/layout";
import { blogPosts } from "@/data/blog-posts";
import { useClientTranslation } from "@/hooks/use-client-translation";
import { ChevronRight } from "lucide-react";

/* -------------------------------------------------------------------------- */
/*                           Breadcrumbs                                      */
/* -------------------------------------------------------------------------- */

function Breadcrumbs({ category }: { category: string }) {
  return (
    <div className="flex items-center gap-2">
      <Link
        to="/blog"
        className="text-sm lg:text-base font-normal leading-relaxed text-black hover:underline"
      >
        Blog
      </Link>
      <ChevronRight className="h-4 w-4 text-black" />
      <span className="text-sm lg:text-base font-normal leading-relaxed text-black">
        {category}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                        Markdown Content Renderer                           */
/* -------------------------------------------------------------------------- */

function RichContent({ content }: { content: string }) {
  const lines = content.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Skip empty lines
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Horizontal rule
    if (line.trim() === "---") {
      elements.push(
        <hr key={i} className="my-8 border-t border-black/10" />
      );
      i++;
      continue;
    }

    // H2 heading
    if (line.startsWith("## ")) {
      const text = line.replace(/^## \**/, "").replace(/\**$/, "");
      elements.push(
        <div key={i} className="pb-3 lg:pb-4 pt-4 lg:pt-5">
          <h2 className="text-xl lg:text-[32px] font-medium leading-[1.2] tracking-[-0.32px] text-black">
            {text}
          </h2>
        </div>
      );
      i++;
      continue;
    }

    // H3 heading
    if (line.startsWith("### ")) {
      const text = line.replace(/^### \**/, "").replace(/\**$/, "");
      elements.push(
        <div key={i} className="pb-2 lg:pb-3 pt-3 lg:pt-4">
          <h3 className="text-lg lg:text-[26px] font-medium leading-[1.3] tracking-[-0.26px] text-black">
            {text}
          </h3>
        </div>
      );
      i++;
      continue;
    }

    // Bold subheading (standalone bold line)
    if (line.startsWith("**") && line.endsWith("**") && !line.includes("\n")) {
      const text = line.replace(/^\*\*/, "").replace(/\*\*$/, "");
      elements.push(
        <div key={i} className="pb-3 lg:pb-4 pt-4 lg:pt-5">
          <h4 className="text-lg lg:text-[22px] font-medium leading-[1.4] tracking-[-0.22px] text-black">
            {text}
          </h4>
        </div>
      );
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith("> ")) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith("> ")) {
        quoteLines.push(lines[i].replace(/^> /, ""));
        i++;
      }
      const quoteText = quoteLines.join("\n").replace(/^\*\*|^\*|`/g, "").trim();
      elements.push(
        <div key={`quote-${i}`} className="flex items-start gap-3 lg:gap-5 py-6 lg:py-9 pr-3 lg:pr-5">
          <div className="w-0.5 self-stretch shrink-0 bg-black/15" />
          <p className="flex-1 text-base lg:text-xl italic leading-7 text-black">
            {quoteText}
          </p>
        </div>
      );
      continue;
    }

    // Unordered list
    if (line.startsWith("* ") || line.startsWith("- ")) {
      const listItems: string[] = [];
      while (
        i < lines.length &&
        (lines[i].startsWith("* ") || lines[i].startsWith("- "))
      ) {
        listItems.push(lines[i].replace(/^\* |^- /, ""));
        i++;
      }
      elements.push(
        <ul key={`list-${i}`} className="pb-4 pl-6 list-disc space-y-2">
          {listItems.map((item, idx) => (
            <li
              key={idx}
              className="text-base font-normal leading-relaxed text-black"
            >
              {renderInlineMarkdown(item)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\d+\.\s/, ""));
        i++;
      }
      elements.push(
        <ol key={`olist-${i}`} className="pb-4 pl-6 list-decimal space-y-2">
          {listItems.map((item, idx) => (
            <li
              key={idx}
              className="text-base font-normal leading-relaxed text-black"
            >
              {renderInlineMarkdown(item)}
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Table
    if (line.includes("|") && line.trim().startsWith("|")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].includes("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      // Filter out separator rows
      const dataRows = tableLines.filter((l) => !l.match(/^\|[\s-|]+\|$/));
      if (dataRows.length > 0) {
        const headers = dataRows[0]
          .split("|")
          .filter((c) => c.trim())
          .map((c) => c.trim().replace(/^\*\*|\*\*$/g, ""));
        const rows = dataRows.slice(1).map((row) =>
          row
            .split("|")
            .filter((c) => c.trim())
            .map((c) => c.trim())
        );
        elements.push(
          <div key={`table-${i}`} className="py-6 overflow-x-auto">
            <table className="w-full border-collapse border border-black/15 text-sm">
              <thead>
                <tr className="bg-black/5">
                  {headers.map((h, idx) => (
                    <th
                      key={idx}
                      className="border border-black/15 px-4 py-3 text-left font-semibold text-black"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ridx) => (
                  <tr key={ridx}>
                    {row.map((cell, cidx) => (
                      <td
                        key={cidx}
                        className="border border-black/15 px-4 py-3 text-black"
                      >
                        {renderInlineMarkdown(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }
    }

    // Regular paragraph
    elements.push(
      <div key={i} className="pb-2.5">
        <p className="text-base font-normal leading-relaxed text-black">
          {renderInlineMarkdown(line)}
        </p>
      </div>
    );
    i++;
  }

  return <div className="flex flex-col items-start w-full">{elements}</div>;
}

/** Render inline markdown (bold, italic, links) */
function renderInlineMarkdown(text: string): React.ReactNode {
  // Simple approach: split by bold markers
  const parts: React.ReactNode[] = [];
  const segments = text.split(/(\*\*[^*]+\*\*)/g);
  segments.forEach((seg, idx) => {
    if (seg.startsWith("**") && seg.endsWith("**")) {
      parts.push(
        <strong key={idx} className="font-semibold">
          {seg.slice(2, -2)}
        </strong>
      );
    } else {
      // Handle links: [text](url)
      const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      let lastIndex = 0;
      let match;
      while ((match = linkRegex.exec(seg)) !== null) {
        if (match.index > lastIndex) {
          parts.push(seg.slice(lastIndex, match.index));
        }
        parts.push(
          <a
            key={`link-${idx}-${match.index}`}
            href={match[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-purple-primary"
          >
            {match[1]}
          </a>
        );
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < seg.length) {
        parts.push(seg.slice(lastIndex));
      }
    }
  });
  return parts;
}

/* -------------------------------------------------------------------------- */
/*                           Blog Post Page                                   */
/* -------------------------------------------------------------------------- */

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { i18n } = useClientTranslation();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);

  // When the site language changes, switch to the translated version if available
  useEffect(() => {
    if (!post) return;
    const currentLang = i18n.language;
    if (post.language !== currentLang && post.translatedSlug) {
      const translated = blogPosts.find((p) => p.slug === post.translatedSlug);
      if (translated) {
        navigate(`/blog/${translated.slug}`, { replace: true });
      }
    }
  }, [i18n.language, post, navigate]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <PageLayout activePage="blog">
      <main className="flex-1">
        {/* Header Section */}
        <section className="w-full bg-white px-4 md:px-8 lg:px-12 py-8 lg:py-12">
          <div className="flex flex-col gap-6 lg:gap-10 items-center max-w-container mx-auto">
            {/* Header Content */}
            <div className="flex flex-col gap-4 lg:gap-6 items-start max-w-6xl w-full">
              {/* Title area */}
              <div className="flex flex-col gap-3 lg:gap-4 items-start w-full">
                <Breadcrumbs category={post.category} />
                <h1 className="text-2xl md:text-3xl lg:text-[40px] font-medium leading-[1.25] tracking-[-0.4px] text-black">
                  {post.title}
                </h1>
              </div>

            </div>

            {/* Hero Image - only shown when post has an image */}
            {post.image && (
              <div className="w-full">
                <div className="aspect-[4/3] lg:aspect-[1280/600] w-full overflow-hidden rounded-lg bg-gray-100">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Content Section */}
        <section className="w-full bg-white px-4 md:px-8 lg:px-12 py-6 lg:py-10">
          <div className="flex flex-col items-center max-w-container mx-auto">
            <div className="flex flex-col gap-6 lg:gap-10 items-center max-w-6xl w-full">
              {/* Rich text content */}
              <RichContent content={post.content} />
            </div>
          </div>
        </section>
      </main>
    </PageLayout>
  );
}
